import { onScopeDispose, readonly, ref, watch, type Ref } from 'vue'

/** The rotating example text shown while the field is empty and active. */
export interface RotatingText {
  /** The line to render right now. */
  text: Readonly<Ref<string>>
}

const FALLBACK_INTERVAL_MS = 3200

/** HAZARD: a CSS minifier rewrites `3200ms` to `3.2s`, so parsing the number alone reads 3.2 ms in production only. */
function readInterval(): number {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') {
    return FALLBACK_INTERVAL_MS
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--placeholder-rotate-interval')
    .trim()
  const match = /^(\d*\.?\d+)(ms|s)$/.exec(raw)
  if (match === null) return FALLBACK_INTERVAL_MS
  const value = Number.parseFloat(match[1]!)
  if (!Number.isFinite(value) || value <= 0) return FALLBACK_INTERVAL_MS
  return match[2] === 's' ? value * 1000 : value
}

const INTERVAL_MS = readInterval()

function holdsOneLine(): boolean {
  if (typeof matchMedia !== 'function') return true
  return matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Cycles a curated list of example lines in order for as long as `active` is true. */
export function useRotatingText(lines: Ref<readonly string[]>, active: Ref<boolean>): RotatingText {
  const index = ref(0)
  const text = ref<string>(lines.value[0] ?? '')
  let timer: ReturnType<typeof setInterval> | null = null

  function stop(): void {
    if (timer === null) return
    clearInterval(timer)
    timer = null
  }

  function start(): void {
    stop()
    index.value = 0
    text.value = lines.value[0] ?? ''
    if (lines.value.length === 0 || holdsOneLine()) return
    timer = setInterval(() => {
      index.value = (index.value + 1) % lines.value.length
      text.value = lines.value[index.value] ?? ''
    }, INTERVAL_MS)
  }

  watch(active, (on) => (on ? start() : stop()), { immediate: true })

  watch(lines, () => {
    if (active.value) start()
  })

  onScopeDispose(stop)

  return { text: readonly(text) }
}
